import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, timer, throwError } from 'rxjs';
import { tap, map, timeout, retry, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface AuthStatus {
  authenticated: boolean;
  expiresAt?: number;
  reason?: string;
  tokenRefreshed?: boolean;
  userInfo?: {
    id: string;
    email?: string;
    display_name?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStatusSubject = new BehaviorSubject<boolean>(false);
  public authStatus$ = this.authStatusSubject.asObservable();
  private userInfoSubject = new BehaviorSubject<AuthStatus['userInfo'] | null>(null);
  public userInfo$ = this.userInfoSubject.asObservable();

  constructor(private http: HttpClient) {
    // Check authentication status on service initialization
    this.checkAuthStatus().subscribe();
  }

  /**
   * Initiates Spotify OAuth flow.
   * Opens in a popup window to avoid iframe restrictions from Spotify.
   */
  login(): void {
    const loginUrl = `${environment.apiBaseUrl}/auth/login`;

    if (window.self !== window.top) {
      // Inside an iframe (e.g. Home Assistant Android app).
      // The HA webview opens auth in the system browser (Chrome).
      // Use a pairing code so the iframe can detect when auth completes.
      const pairCode = Math.random().toString(36).substring(2, 15);
      window.open(`${loginUrl}?pairCode=${pairCode}`);
      this.pollPairStatus(pairCode);
    } else {
      window.location.href = loginUrl;
    }
  }

  /**
   * Initiates Spotify OAuth flow with a specific pairing code.
   * Used by the PairComponent when user scans QR code on their phone.
   */
  loginWithPairCode(pairCode: string): void {
    const loginUrl = `${environment.apiBaseUrl}/auth/login?pairCode=${pairCode}`;
    window.location.href = loginUrl;
  }

  /**
   * Poll the backend for pairing code completion (iframe auth flow).
   * When the user completes auth in the external browser, the backend
   * sets a session cookie and we pick it up here.
   * Returns a cleanup function to stop polling.
   */
  pollPairStatus(pairCode: string): () => void {
    console.log('MusicServed: Started polling for pair code:', pairCode);
    const interval = setInterval(() => {
      this.http.get<{ authenticated: boolean }>(
        `${environment.apiBaseUrl}/auth/pair-status/${pairCode}`,
        { withCredentials: true }
      ).subscribe({
        next: response => {
          console.log('MusicServed: Pair status response:', response);
          if (response.authenticated) {
            console.log('MusicServed: Pairing successful! Checking auth status...');
            clearInterval(interval);
            this.checkAuthStatus().subscribe({
              next: isAuth => console.log('MusicServed: Auth status after pairing:', isAuth),
              error: err => console.error('MusicServed: Auth status check failed:', err)
            });
          }
        },
        error: err => console.error('MusicServed: Pair status poll error:', err)
      });
    }, 2000);

    // Stop polling after 5 minutes
    const timeout = setTimeout(() => clearInterval(interval), 5 * 60 * 1000);

    // Return cleanup function
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }

  /**
   * Handles the OAuth callback by checking if authentication was successful
   */
  async handleCallback(): Promise<boolean> {
    try {
      console.log('AuthService.handleCallback() called');
      const authStatus = await this.checkAuthStatus().toPromise();
      console.log('Auth status from checkAuthStatus:', authStatus);
      return authStatus || false;
    } catch (error) {
      console.error('Error handling auth callback:', error);
      return false;
    }
  }

  /**
   * Logout user by calling backend logout endpoint
   */
  logout(): Observable<void> {
    return this.http.post<any>(`${environment.apiBaseUrl}/auth/logout`, {}, {
      withCredentials: true
    }).pipe(
      tap(() => {
        this.authStatusSubject.next(false);
        this.userInfoSubject.next(null);
      }),
      map(() => void 0)
    );
  }

  /**
   * Check current authentication status with backend
   */
  checkAuthStatus(): Observable<boolean> {
    console.log('Making request to:', `${environment.apiBaseUrl}/auth/status`);
    return this.http.get<AuthStatus>(`${environment.apiBaseUrl}/auth/status`, {
      withCredentials: true
    }).pipe(
      // 503 = transient backend-side token-refresh failure; 0 = network error.
      // Retry those briefly — a blip must not de-auth the UI or break guards.
      retry({
        count: 2,
        delay: (error: unknown, retryCount: number) => {
          const status = (error as HttpErrorResponse).status;
          if (status === 503 || status === 0) {
            console.warn(`[recovery] auth status transient failure (${status}), retry ${retryCount}`);
            return timer(1000 * retryCount);
          }
          return throwError(() => error);
        }
      }),
      tap(response => {
        console.log('Auth status response:', response);
        
        // Update user info if available
        if (response.userInfo) {
          this.userInfoSubject.next(response.userInfo);
        }
        
        // Log token refresh events
        if (response.tokenRefreshed) {
          console.log('Token was automatically refreshed');
        }
      }),
      map(response => response.authenticated),
      tap(isAuthenticated => {
        console.log('Mapped authentication status:', isAuthenticated);
        this.authStatusSubject.next(isAuthenticated);
        
        // Clear user info if not authenticated
        if (!isAuthenticated) {
          this.userInfoSubject.next(null);
        }
      }),
      // Exhausted transient retries: keep the last known auth state instead of
      // erroring — AuthGuard and boot-time subscribers must survive a blip.
      catchError((error: HttpErrorResponse) => {
        if (error.status === 503 || error.status === 0) {
          console.warn('[recovery] auth status unavailable, keeping last known state:', this.authStatusSubject.value);
          return of(this.authStatusSubject.value);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current authentication state
   */
  isAuthenticated(): boolean {
    return this.authStatusSubject.value;
  }

  /**
   * Refresh access token if needed
   */
  refreshToken(): Observable<void> {
    return this.http.post<any>(`${environment.apiBaseUrl}/auth/refresh`, {}, {
      withCredentials: true
    }).pipe(
      tap(() => {
        this.authStatusSubject.next(true);
      }),
      map(() => void 0)
    );
  }

  /**
   * Get access token (for playback SDK)
   * Note: This returns a dummy token since actual tokens are handled by backend
   */
  getAccessToken(): Observable<{ token: string; expiresAt: number | null }> {
    // Timeout + one retry: the SDK's getOAuthToken callback must always be
    // answered, so this fetch can never be allowed to hang indefinitely
    // (e.g. stale HTTP/2 connections through a proxy can stall ~30s).
    // expiresAt bounds the client-side token cache by real validity.
    return this.http.get<{ token: string; expiresAt?: number | null }>(`${environment.apiBaseUrl}/auth/token`, {
      withCredentials: true
    }).pipe(
      timeout(5000),
      retry({ count: 1, delay: 1000 }),
      map(response => ({ token: response.token, expiresAt: response.expiresAt ?? null }))
    );
  }

  /**
   * Get current user info
   */
  getUserInfo(): AuthStatus['userInfo'] | null {
    return this.userInfoSubject.value;
  }
}